import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import FootworkLogo from "@/components/FootworkLogo";
import {
  LayoutDashboard,
  Boxes,
  PackagePlus,
  Hammer,
  History,
  Users as UsersIcon,
  LogOut,
  Bell,
  AlertTriangle,
  Building2,
  BarChart3,
  ShoppingCart,
  QrCode,
  Bug,
  Monitor,
  Cog,
  Menu,
  X,
  ClipboardCheck,
  Camera,
  HardHat,
  FileText,
  DollarSign,
  Sparkles,
} from "lucide-react";
import StockThumb from "@/components/StockThumb";
import BugReportButton from "@/components/BugReportButton";

const ROLE_LABEL = {
  warehouse_manager: "Warehouse Mgr",
  operations_manager: "Operations Mgr",
  staff: "Staff",
};

// App version + build fingerprint are now fetched live from GET /api/version
// (backend derives the SHA from git HEAD at container boot), so we don't
// have to remember to bump a constant on every deploy — the sidebar chip
// updates automatically and the "new version" refresh toast fires when
// the SHA changes between polls.
const detectEnv = () => {
  if (typeof window === "undefined") return { label: "—", isPreview: true };
  const h = window.location.hostname.toLowerCase();
  if (h.includes("preview") || h.includes("localhost")) {
    return { label: "Preview", isPreview: true };
  }
  return { label: "Production", isPreview: false };
};

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard", end: true },
  { to: "/take", label: "Take Stock", icon: Hammer, tid: "nav-take" },
  { to: "/receive", label: "Receive", icon: PackagePlus, tid: "nav-receive" },
  { to: "/catalog", label: "Inventory", icon: Boxes, tid: "nav-catalog", managerOnly: true },
  { to: "/assets", label: "Assets", icon: Monitor, tid: "nav-assets" },
  { to: "/machinery", label: "Machinery", icon: Cog, tid: "nav-machinery" },
  { to: "/technicians", label: "Technicians", icon: HardHat, tid: "nav-technicians" },
  { to: "/service-agreements", label: "Agreements", icon: FileText, tid: "nav-service-agreements" },
  { to: "/site-services", label: "Site Services", icon: Building2, tid: "nav-site-services" },
  { to: "/suppliers", label: "Suppliers", icon: Building2, tid: "nav-suppliers", managerOnly: true },
  { to: "/orders", label: "Orders & POs", icon: ShoppingCart, tid: "nav-orders", managerOnly: true },
  { to: "/reports", label: "Reports", icon: BarChart3, tid: "nav-reports", managerOnly: true },
  { to: "/labels", label: "QR Labels", icon: QrCode, tid: "nav-labels", managerOnly: true },
  { to: "/history", label: "History", icon: History, tid: "nav-history" },
  { to: "/team", label: "Team", icon: UsersIcon, tid: "nav-team", managerOnly: true },
  { to: "/bug-reports", label: "Bug Reports", icon: Bug, tid: "nav-bugs", managerOnly: true },
  { to: "/stocktake", label: "Stocktake", icon: ClipboardCheck, tid: "nav-stocktake", managerOnly: true },
  { to: "/quick-photos", label: "Quick Photos", icon: Camera, tid: "nav-quick-photos", managerOnly: true },
  { to: "/quick-costs", label: "Quick Costs", icon: DollarSign, tid: "nav-quick-costs", managerOnly: true },
  { to: "/snap-import", label: "Snap Import", icon: Sparkles, tid: "nav-snap-import", managerOnly: true },
];

const Layout = ({ children }) => {
  const { user, logout, isManager } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [lowStock, setLowStock] = useState([]);
  const [expirySoon, setExpirySoon] = useState([]);
  const [openBugs, setOpenBugs] = useState(0);
  const [alertsOpen, setAlertsOpen] = useState(false);
  // Sender-health check — fired once for managers on mount. Drives the red
  // banner above <main> when SENDER_EMAIL is still the shared Resend default,
  // so admins are nudged to verify a real domain before another PO bounces.
  const [senderHealth, setSenderHealth] = useState(null);
  // Live app version pulled from /api/version — displayed in the sidebar
  // footer and used to detect stale bundles. Object shape: {version,
  // build_sha, build_date}. Set on first successful poll and refreshed on
  // every subsequent poll so the chip stays current.
  const [appVersion, setAppVersion] = useState(null);
  // Version poll — if the backend's build_sha diverges from what we
  // loaded on mount, we know a new deploy has landed and this browser is
  // running a stale bundle. Ref (not state) because we only want ONE
  // toast per session, not one per re-render.
  const initialShaRef = React.useRef(null);
  const versionToastShownRef = React.useRef(false);
  // Mobile drawer state — sidebar is hidden on <lg and only slides in when
  // the user taps the hamburger. Auto-closes on route change so navigation
  // feels app-native on phones.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
    setAlertsOpen(false);
  }, [location.pathname]);

  const refresh = async () => {
    // Version check — fires on every poll cycle. Compares the backend's
    // reported build_sha to the first value we saw at mount; a mismatch
    // means a new deploy shipped since this browser loaded, so we prompt
    // for a refresh. Only ever shows one toast per session.
    try {
      const { data } = await api.get("/version");
      setAppVersion(data);
      const curSha = data?.build_sha;
      const curVer = data?.version;
      if (curSha) {
        if (initialShaRef.current == null) {
          initialShaRef.current = curSha;
        } else if (
          initialShaRef.current !== curSha &&
          !versionToastShownRef.current
        ) {
          versionToastShownRef.current = true;
          toast(
            (t) => (
              <div className="flex items-center gap-3">
                <div className="text-sm">
                  <b>New version available</b>
                  <div className="text-xs text-[#607087]">
                    {initialShaRef.current} → {curSha}
                    {curVer ? ` (${curVer})` : ""}
                  </div>
                </div>
                <button
                  data-testid="version-refresh-btn"
                  onClick={() => window.location.reload()}
                  className="nb-btn nb-btn-primary px-3 py-1.5 text-xs font-bold"
                >
                  Refresh
                </button>
              </div>
            ),
            { duration: Infinity, id: "new-version-toast" },
          );
        }
      }
    } catch (_) {
      /* transient — try again on the next tick */
    }
    try {
      const { data } = await api.get("/dashboard/stats");
      setLowStock(Array.isArray(data?.low_stock_items) ? data.low_stock_items : []);
    } catch (_) {
      /* transient — polling will retry in 60s */
    }
    try {
      const { data } = await api.get("/expiry/soon");
      // Defensive: some edge/CDN error pages come back with a 200 + HTML
      // body, which has a truthy `.length` (character count) but no `.map`.
      // Guarantee the state is always an array so downstream renders never
      // blow up with "h.map is not a function" and show a phantom 300k badge.
      setExpirySoon(Array.isArray(data) ? data : []);
    } catch (_) {
      /* transient — polling will retry in 60s */
    }
    if (isManager) {
      try {
        const { data } = await api.get("/bug-reports/counts");
        setOpenBugs(data.open || 0);
      } catch (_) {
        /* transient — polling will retry in 60s */
      }
      // Sender-health check only makes sense for managers and doesn't
      // change often — fetch inside refresh so it also self-heals if the
      // env var is fixed and services are restarted.
      try {
        const { data } = await api.get("/settings/sender-health");
        setSenderHealth(data);
      } catch (_) {
        /* transient */
      }
    }
  };

  useEffect(() => {
    const t = setInterval(refresh, 60_000);
    // Also refresh when the user returns to the tab/window — they may have
    // just changed a threshold or added/consumed stock on another device.
    const onFocus = () => refresh();
    // App-wide "stock-updated" event: fired by StockFormDialog after save,
    // by StockOut after taking stock, etc. Guarantees the bell badge stays
    // in sync with user actions without waiting for the 60s poll.
    const onStockUpdated = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("stock-updated", onStockUpdated);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("stock-updated", onStockUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch alerts on every route change (and once on mount) — after saving
  // a threshold, adding a low-qty item, or taking stock, the user typically
  // navigates and would expect the bell badge to reflect their action
  // immediately (rather than waiting up to 60s for the poll).
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Refresh the moment the user opens the alerts panel — tapping the bell
  // should always show current state.
  useEffect(() => {
    if (alertsOpen) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsOpen]);

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Mobile top-bar with hamburger + logo + bell. Only visible on <lg. */}
      <div
        data-testid="mobile-topbar"
        className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between gap-3 px-4 py-3 glass-sidebar text-white"
      >
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileNavOpen(true)}
          className="w-10 h-10 rounded-xl border-2 border-white/30 flex items-center justify-center hover:bg-white/10"
          aria-label="Open navigation menu"
        >
          <Menu size={22} strokeWidth={2.5} />
        </button>
        <FootworkLogo className="h-8" variant="full" />
        <button
          data-testid="mobile-alerts-bell"
          onClick={() => setAlertsOpen((v) => !v)}
          className="relative w-10 h-10 rounded-xl border-2 border-white/30 flex items-center justify-center hover:bg-white/10"
          aria-label="Alerts"
        >
          <Bell size={18} strokeWidth={2.5} />
          {lowStock.length + expirySoon.length > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 rounded-full bg-[#E5263F] text-white text-xs font-bold flex items-center justify-center px-1 border-2 border-black pulse-danger"
              data-testid="mobile-alerts-badge"
            >
              {lowStock.length + expirySoon.length}
            </span>
          )}
        </button>
      </div>

      {/* Backdrop for the mobile drawer */}
      {mobileNavOpen && (
        <div
          data-testid="mobile-nav-backdrop"
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside
        data-testid="sidebar"
        className={`w-72 shrink-0 glass-sidebar text-white p-5 flex flex-col gap-4
          fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ease-out
          lg:static lg:z-auto lg:transform-none lg:transition-none lg:h-screen lg:sticky lg:top-0
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex items-center justify-between">
          <FootworkLogo className="h-10" variant="full" />
          <button
            data-testid="alerts-bell"
            onClick={() => setAlertsOpen((v) => !v)}
            className="relative w-11 h-11 rounded-xl border-2 border-white/30 hidden lg:flex items-center justify-center hover:bg-white/10"
          >
            <Bell size={20} strokeWidth={2.5} />
            {lowStock.length + expirySoon.length > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 rounded-full bg-[#E5263F] text-white text-xs font-bold flex items-center justify-center px-1 border-2 border-black pulse-danger"
                data-testid="alerts-badge"
              >
                {lowStock.length + expirySoon.length}
              </span>
            )}
          </button>
          {/* Close button inside the drawer, only on mobile */}
          <button
            data-testid="mobile-nav-close"
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden w-10 h-10 rounded-xl border-2 border-white/30 flex items-center justify-center hover:bg-white/10"
            aria-label="Close navigation menu"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>
        <div
          className="label-eyebrow text-white/50 -mt-2"
          data-testid="sidebar-version-chip"
          title={
            appVersion?.build_sha
              ? `build ${appVersion.build_sha}${appVersion.build_date ? ` · ${appVersion.build_date}` : ""}`
              : undefined
          }
        >
          stock control · {appVersion?.version || "…"}
        </div>

        <div
          data-testid="current-user-chip"
          className="rounded-2xl border-2 border-white/20 p-3 flex items-center gap-3 bg-white/5"
        >
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-white font-display text-xl"
            style={{ background: user?.color || "#228EBF", color: "#fff" }}
          >
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg truncate">{user?.name}</div>
            <div className="label-eyebrow text-white/60">
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
        </div>

        <nav
          className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto scrollbar-clean -mx-1 px-1"
          data-testid="sidebar-nav"
        >
          {navItems
            .filter((it) => !it.managerOnly || isManager)
            .map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl border-2 transition-all duration-100 shrink-0 ${
                    isActive
                      ? "nav-active"
                      : "border-transparent hover:bg-white/10 text-white"
                  }`
                }
              >
                <it.icon size={18} strokeWidth={2.25} />
                <span className="font-display text-[15px]">{it.label}</span>
              </NavLink>
            ))}
        </nav>

        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="nb-btn nb-btn-danger px-4 py-3 flex items-center justify-center gap-2 shrink-0"
        >
          <LogOut size={18} strokeWidth={2.5} />
          Sign out
        </button>

        {(() => {
          const env = detectEnv();
          return (
            <div
              data-testid="app-version-tag"
              className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-white/20 bg-white/5 text-[10px] tracking-wide"
            >
              <span
                className="font-mono text-white/80 truncate"
                title={
                  appVersion
                    ? `${appVersion.version} · ${appVersion.build_sha} · built ${appVersion.build_date}`
                    : ""
                }
              >
                {appVersion
                  ? `${appVersion.version} · ${appVersion.build_sha}`
                  : "…"}
              </span>
              <span
                data-testid="app-env-tag"
                className={`px-1.5 py-0.5 rounded font-bold uppercase ${
                  env.isPreview
                    ? "bg-[#FFD37E] text-black"
                    : "bg-[#0B6B2A] text-white"
                }`}
              >
                {env.label}
              </span>
            </div>
          );
        })()}
      </aside>

      <main
        className="flex-1 overflow-x-hidden relative pt-16 lg:pt-0"
        data-testid="main-area"
      >
        {isManager && senderHealth?.is_default && (
          <div
            data-testid="sender-health-banner"
            className="bg-[#8A1F1F] text-white px-4 py-2 text-xs sm:text-sm flex items-center gap-2 border-b-2 border-black"
          >
            <AlertTriangle size={16} strokeWidth={2.5} className="shrink-0" />
            <div className="flex-1">
              <b>PO emails will bounce</b> — outbound sender is still{" "}
              <span className="font-mono">{senderHealth.sender_email}</span>,
              which corporate mailboxes block. Verify your domain in Resend
              and set <span className="font-mono">SENDER_EMAIL</span> in
              production.
            </div>
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noopener noreferrer"
              className="nb-btn bg-white text-black px-2 py-1 text-xs font-bold no-underline shrink-0"
              data-testid="sender-health-cta"
            >
              Open Resend
            </a>
          </div>
        )}
        {children}

        {alertsOpen && (
          <div
            className="fixed top-16 right-3 lg:top-4 lg:right-4 w-[92vw] max-w-[380px] z-40 nb-card p-4"
            data-testid="alerts-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl flex items-center gap-2">
                <AlertTriangle size={20} strokeWidth={2.5} className="text-[#E5263F]" />
                Alerts
              </h3>
              <button
                data-testid="alerts-close"
                onClick={() => setAlertsOpen(false)}
                className="nb-btn bg-white p-1.5 text-xs"
              >
                Close
              </button>
            </div>
            {lowStock.length === 0 && expirySoon.length === 0 && openBugs === 0 ? (
              <div className="text-sm text-[#6B6B6B] py-4 text-center">
                All good — nothing below threshold or expiring soon.
              </div>
            ) : (
              <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto scrollbar-clean">
                {isManager && openBugs > 0 && (
                  <button
                    data-testid="alerts-bugs-link"
                    onClick={() => {
                      setAlertsOpen(false);
                      nav("/bug-reports");
                    }}
                    className="nb-card-sm p-2 flex items-center gap-3 text-left"
                  >
                    <Bug size={20} strokeWidth={2.5} className="text-[#E5263F]" />
                    <div className="flex-1">
                      <div className="font-display text-sm">
                        {openBugs} open bug report{openBugs === 1 ? "" : "s"}
                      </div>
                      <div className="label-eyebrow text-[#607087]">
                        Tap to review
                      </div>
                    </div>
                  </button>
                )}
                {lowStock.length > 0 && (
                  <>
                    <div className="label-eyebrow text-[#E5263F]">Low stock</div>
                    {lowStock.map((it) => (
                      <button
                        key={`low-${it.id}`}
                        type="button"
                        data-testid={`alerts-low-${it.id}`}
                        onClick={() => {
                          setAlertsOpen(false);
                          if (isManager) {
                            nav(`/orders?focus=${it.id}`);
                          } else {
                            nav(`/take?focus=${it.id}`);
                          }
                        }}
                        className="flex items-center gap-3 nb-card-sm p-2 text-left hover:bg-[#FFF0F1] hover:ring-2 hover:ring-[#E5263F]/30 transition"
                        title={isManager ? "Open reorder draft" : "Find this item on Take stock"}
                      >
                        <StockThumb item={it} size="h-12 w-12" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-sm truncate">{it.name}</div>
                          <div className="label-eyebrow">
                            {it.type}
                            {isManager ? " · tap to reorder" : " · tap to view"}
                          </div>
                        </div>
                        <div className="font-mono-num font-bold text-lg px-2 py-0.5 rounded bg-[#FFEC8A]">
                          {it.current_qty}
                        </div>
                      </button>
                    ))}
                  </>
                )}
                {expirySoon.length > 0 && (
                  <>
                    <div className="label-eyebrow text-[#9A6F00] mt-1">Expiring soon</div>
                    {expirySoon.map((it) => (
                      <button
                        key={`exp-${it.id}`}
                        type="button"
                        data-testid={`expiry-row-${it.id}`}
                        onClick={() => {
                          setAlertsOpen(false);
                          nav(`/take?focus=${it.id}`);
                        }}
                        className="flex items-center gap-3 nb-card-sm p-2 text-left hover:bg-[#FFF7E0] hover:ring-2 hover:ring-[#9A6F00]/30 transition"
                        title="Use up before expiry"
                      >
                        <StockThumb item={it} size="h-12 w-12" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-sm truncate">{it.name}</div>
                          <div className="label-eyebrow">
                            {it._days_to_expiry < 0
                              ? `Expired ${Math.abs(it._days_to_expiry)}d ago`
                              : `${it._days_to_expiry}d left · ${it.expiry_date}`}
                            {" · tap to take"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>
      <BugReportButton />
    </div>
  );
};

export default Layout;
